import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import Button from '../../../../../components/Form/Button';
import { InfoModal } from '../../../../../components/Modal/InfoModal';
import { Icon, Icons } from 'components/Icon';
import { useUploadSignedWill } from 'common/queries-and-mutations/admin/customer';
import { useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import PDFImage from '../../../../../assets/pdf-image-1.png';

type Props = {
  onSuccess: () => void;
  onClose: () => void;
  className?: string;
  customerId: number;
};

export default function UploadSignedWillModal({
  onSuccess,
  onClose,
  className = 'w-[20%]',
  customerId
}: Readonly<Props>) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadFile, setUploadFile] = useState<File>();
  const { user } = useAppSelector(selectAccountDetails);
  const { mutate, isSuccess, isError } = useUploadSignedWill();

  const removeFile = () => {
    setUploadFile(undefined);
  };

  const validateFile = async (files: File[]) => {
    if (!files.length) {
      return;
    }
    const file = files[0];

    const fileType = file.name.substring(file.name.lastIndexOf('.') + 1);
    if (fileType !== 'pdf') {
      toast.error(<Alert type="error" message="Invalid file selected" />);
      return;
    }
    setUploadFile(file);
  };

  const onDrop = useCallback(async (accepedFiles: File[]) => {
    validateFile(accepedFiles);
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop
    // noClick: true
  });

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  const handleSubmit = (e: FormEvent) => {
    setIsSubmitting(true);
    e.preventDefault();
    const formData = new FormData();
    formData.append('file', uploadFile!);
    formData.append('customerId', customerId.toString());
    formData.append('staffId', user.id!.toString());
    mutate({ formData });
  };

  const disableButton = useMemo(
    () => !uploadFile || isSubmitting,
    [uploadFile, isSubmitting]
  );

  return (
    <InfoModal width={className} className="h-fit rounded-lg py-2">
      <div className="flex w-full flex-col items-center bg-white font-circular text-2xl font-bold text-[#353535]">
        <form onSubmit={handleSubmit} className="w-full">
          <div className="flex items-center justify-between border-b border-[#E5E5E5] p-3 px-5">
            <p className="text-left text-base font-medium">
              Upload Signed Will
            </p>
            <div className="" role="button" onClick={onClose}>
              <Icon name={Icons.Close} />
            </div>
          </div>
          <div className="p-5">
            <div className="group relative z-0 mb-6 mt-3 w-full">
              {!uploadFile ? (
                <div
                  className="flex h-full flex-col items-center justify-center rounded-2xl border border-dashed border-[#C6C6C6] bg-white py-0"
                  {...getRootProps()}
                  onClick={open}
                  role="button"
                  // onKeyDown={open}
                  // tabIndex={0}
                >
                  <div className="mt-4 rounded-full bg-[#E300000F] p-4">
                    <Icon name={Icons.Upload} />
                  </div>
                  <p className="mt-2 text-sm text-primary-800">PDF ONLY</p>
                  <div className="my-6 flex gap-1">
                    <label
                      className="cursor-pointer text-sm font-bold text-primary-800"
                      // htmlFor="upload"
                    >
                      Select file
                    </label>
                    <input
                      type="file"
                      hidden
                      // id="upload"
                      accept=".pdf"
                      {...getInputProps()}
                    />
                    <p className="text-sm text-[#002855]">or drag and drop</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between rounded-2xl border border-dashed border-[#C6C6C6] bg-white p-3">
                  <div className="flex items-center gap-2">
                    <img src={PDFImage} className="h-5 w-5" />
                    <p className="text-sm">{uploadFile.name}</p>
                  </div>
                  <button onClick={removeFile}>
                    <Icon name={Icons.Delete} />
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="mr-6 flex items-center justify-end gap-4 pb-4">
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="mb-3 w-[10rem] rounded text-sm font-normal text-white"
              label={'Upload'}
              loading={isSubmitting}
              disabled={disableButton}
            ></Button>
          </div>
        </form>
      </div>
    </InfoModal>
  );
}
